import type { Feedback } from './check-session';

/**
 * Ton und Vibration.
 *
 * Der Bildschirm ist bei Sonne kaum ablesbar. Das hörbare und spürbare
 * Signal ist am Eingang oft das eigentliche Ergebnis.
 *
 * iOS gibt Audio erst nach einer Nutzergeste frei. Würde der Klang erst
 * beim ersten ROT erzeugt, bliebe genau dieses erste ROT stumm. Deshalb
 * `unlock()` bei der Anmeldung.
 */
export function createFeedback(): Feedback & { unlock(): void } {
  let ctx: AudioContext | null = null;

  /**
   * Vibration verlangt eine vorangegangene Nutzergeste; ohne sie verwirft
   * der Browser den Aufruf und schreibt eine Meldung in die Konsole. Auf dem
   * Gerät am Eingang ist die Geste immer da (Anmeldung, Quittieren), im
   * Testaufbau am Rechner nicht, und dann füllt sich die Konsole mit
   * Meldungen, die nach einem Fehler aussehen und keiner sind.
   */
  let gestured = false;
  if (typeof window !== 'undefined') {
    const merken = (): void => {
      gestured = true;
    };
    window.addEventListener('pointerdown', merken, { once: true });
    window.addEventListener('keydown', merken, { once: true });
  }

  function audio(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctx) ctx = new Ctor();
    return ctx;
  }

  function tone(frequency: number, durationMs: number, delayMs = 0): void {
    const context = audio();
    if (!context) return;

    const start = context.currentTime + delayMs / 1000;
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    // Weiche Flanken: ein harter Rechteckeinsatz klingt auf kleinen
    // Lautsprechern nach Knacken statt nach Signal.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.3, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + durationMs / 1000);

    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + durationMs / 1000 + 0.02);
  }

  function vibrate(pattern: number | number[]): void {
    if (!gestured) return;
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  }

  return {
    unlock(): void {
      // Wird aus einer Nutzergeste heraus gerufen (Anmeldung, Entsperren).
      // damit ist auch die Vibration freigegeben.
      gestured = true;
      const context = audio();
      if (context?.state === 'suspended') void context.resume();
    },
    green(): void {
      tone(880, 120);
      vibrate(40);
    },
    yellow(): void {
      // Zwei kurze Töne: hörbar anders als GRÜN, ohne wie eine Ablehnung zu klingen.
      tone(660, 100);
      tone(660, 100, 140);
      vibrate([30, 60, 30]);
    },
    red(): void {
      tone(220, 400);
      vibrate([120, 80, 120]);
    },
  };
}

/** Für Tests und für Geräte ohne Audio. */
export const silentFeedback: Feedback = {
  green: () => undefined,
  yellow: () => undefined,
  red: () => undefined,
};
